export default class GridAnimation {
    static observer: IntersectionObserver | null = null;
    static initialized = false;

    static init(): void {
        if (this.initialized) return;
        this.initialized = true;
        //this.showGrid();
        this.initVideos();
    }

    static showGrid(): void {
        // Nastaví všechny grid itemy rovnou na maximální výšku
        function setMaxHeight() {
            const items = document.querySelectorAll<HTMLElement>('.reference-grid-item');
            if (items.length === 0) return;

            items.forEach((item) => {
                const maxHeight = parseInt(item.getAttribute('data-animation-height-max') || '0', 10);

                // Nastaví rovnou maximální výšku
                item.style.height = `${maxHeight}px`;

                // Přidá třídu is-active
                item.classList.add('is-active');
            });
        }

        // Spustí nastavení maximální výšky
        setMaxHeight();
    }

    private static videoHoverHandlers: WeakMap<
        HTMLElement,
        {
            mouseenter: () => void;
            mouseleave: () => void;
        }
    > = new WeakMap();

    static initVideos(): void {
        const videoContainers = document.querySelectorAll<HTMLElement>('.reference-item__video');
        if (!videoContainers.length) return;

        // IntersectionObserver pro lazy loading
        this.observer = new window.IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const container = entry.target as HTMLElement;
                        const video = container.querySelector<HTMLVideoElement>('video');
                        const source = video?.querySelector<HTMLSourceElement>('source');

                        // Unobserve pokud je video už načtené
                        if (source?.getAttribute('src')) {
                            this.observer?.unobserve(entry.target);

                            // Na mobilu spustit pokud je ve viewportu a není spuštěné
                            if (window.innerWidth <= 991 && video && video.paused) {
                                GridAnimation.playVideoWhenReady(video);
                            }
                            return;
                        }

                        GridAnimation.loadAndPlayVideo(container);
                        this.observer?.unobserve(entry.target);
                    }
                });
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.2,
            },
        );

        videoContainers.forEach((el) => {
            this.observer!.observe(el);

            // Inicializace hover podle aktuální šířky
            if (window.innerWidth > 991) {
                GridAnimation.setupDesktopHover(el);
            }
        });

        // Resize handler s debounce
        let resizeTimer: number;
        let wasDesktop = window.innerWidth > 991;

        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                const isDesktop = window.innerWidth > 991;

                // Reaguj pouze pokud došlo ke změně breakpointu
                if (wasDesktop !== isDesktop) {
                    GridAnimation.handleResize(videoContainers, isDesktop);
                    wasDesktop = isDesktop;
                }
            }, 250);
        });

        // Podpora pro tab-nav__content (přepínání záložek)
        document.querySelectorAll('.tab-nav__content').forEach((tabContent) => {
            tabContent.addEventListener('tab-content-shown', () => {
                tabContent.querySelectorAll<HTMLElement>('.reference-item__video').forEach((el) => {
                    if (!el.querySelector('video source')?.getAttribute('src')) {
                        if (GridAnimation.isElementInViewport(el)) {
                            GridAnimation.loadAndPlayVideo(el);
                        }
                    }
                });
            });
        });
    }

    static playVideoWhenReady(video: HTMLVideoElement): void {
        // Pokud už je připravené, spusť hned
        if (video.readyState >= 3) {
            // HAVE_FUTURE_DATA
            video.currentTime = 0;
            video.play().catch(() => {});
            return;
        }

        // Jinak počkej na canplay event
        const canPlayHandler = () => {
            video.currentTime = 0;
            video.play().catch(() => {});
            video.removeEventListener('canplay', canPlayHandler);
            video.removeEventListener('canplaythrough', canPlayHandler);
        };

        video.addEventListener('canplay', canPlayHandler, { once: true });
        video.addEventListener('canplaythrough', canPlayHandler, { once: true });

        // Fallback timeout - zkus to i tak po 300ms
        setTimeout(() => {
            if (video.paused) {
                video.removeEventListener('canplay', canPlayHandler);
                video.removeEventListener('canplaythrough', canPlayHandler);
                video.currentTime = 0;
                video.play().catch(() => {});
            }
        }, 300);
    }

    static handleResize(videoContainers: NodeListOf<HTMLElement>, isDesktop: boolean): void {
        if (isDesktop) {
            // Přepnutí na desktop
            videoContainers.forEach((container) => {
                const video = container.querySelector<HTMLVideoElement>('video');
                if (!video) return;

                // Zastavit všechna videa
                if (!video.paused) {
                    video.pause();
                    video.currentTime = 0;
                }

                // Nastavit hover pro již načtená videa
                const source = video.querySelector<HTMLSourceElement>('source');
                if (source?.getAttribute('src') && !this.videoHoverHandlers.has(container)) {
                    GridAnimation.setupDesktopHover(container);
                }
            });
        } else {
            // Přepnutí na mobil
            videoContainers.forEach((container) => {
                const video = container.querySelector<HTMLVideoElement>('video');
                const source = video?.querySelector<HTMLSourceElement>('source');
                if (!video) return;

                // Odstranit hover
                GridAnimation.removeDesktopHover(container);

                const hasSource = source?.getAttribute('src');
                const inViewport = GridAnimation.isElementInViewport(container);

                if (hasSource && inViewport) {
                    // Použij helper funkci pro spuštění
                    GridAnimation.playVideoWhenReady(video);
                } else if (!hasSource) {
                    // Video není načtené - znovu observe
                    if (this.observer) {
                        this.observer.observe(container);
                    }
                }
            });
        }
    }

    static setupDesktopHover(container: HTMLElement): void {
        const video = container.querySelector<HTMLVideoElement>('video');
        if (!video) return;

        // Pokud už má handlery, neudělej nic
        if (this.videoHoverHandlers.has(container)) return;

        const handlers = {
            mouseenter: () => {
                GridAnimation.playVideoWhenReady(video);
            },
            mouseleave: () => {
                video.pause();
                video.currentTime = 0;
            },
        };

        container.addEventListener('mouseenter', handlers.mouseenter);
        container.addEventListener('mouseleave', handlers.mouseleave);

        this.videoHoverHandlers.set(container, handlers);
    }

    static removeDesktopHover(container: HTMLElement): void {
        const handlers = this.videoHoverHandlers.get(container);
        if (!handlers) return;

        container.removeEventListener('mouseenter', handlers.mouseenter);
        container.removeEventListener('mouseleave', handlers.mouseleave);

        this.videoHoverHandlers.delete(container);
    }

    static loadAndPlayVideo(container: HTMLElement): void {
        const video = container.querySelector<HTMLVideoElement>('video');
        const source = video?.querySelector<HTMLSourceElement>('source');
        const loader = container.querySelector<HTMLElement>('.reference-item__video-loader');
        if (!video || !source || !source.dataset.src) return;

        // Pokud už je src nastaveno, nic nedělej
        if (source.getAttribute('src')) return;

        // Zobrazit loader
        if (loader) loader.style.display = '';

        // Nastav src
        source.setAttribute('src', source.dataset.src);
        video.load();

        const isMobile = window.innerWidth <= 991;

        // Skrytí loaderu po načtení a případné spuštění videa
        const hideLoaderAndPlay = () => {
            if (loader) loader.style.display = 'none';

            if (isMobile) {
                // Na mobilu spustit automaticky
                GridAnimation.playVideoWhenReady(video);
            } else {
                // Na desktopu nastavit hover handlery
                GridAnimation.setupDesktopHover(container);
            }

            video.removeEventListener('loadeddata', hideLoaderAndPlay);
            video.removeEventListener('canplay', hideLoaderAndPlay);
        };

        video.addEventListener('loadeddata', hideLoaderAndPlay, { once: true });
        video.addEventListener('canplay', hideLoaderAndPlay, { once: true });
    }

    static isElementInViewport(el: HTMLElement): boolean {
        const rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    }
}
